/**
 * Who is signed in, as reactive state.
 *
 * `@simplewebauthn/browser` handles the base64url-to-ArrayBuffer conversions that
 * `navigator.credentials` insists on. It is the browser counterpart of the server library, so
 * the two agree about the wire format by construction rather than by our care.
 */

import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { ApiError, OfflineError, request } from './api.ts';

export type User = {
  id: string;
  username: string;
  avatarEmoji: string;
  role: 'member' | 'admin';
  status: 'pending' | 'active' | 'suspended';
};

export type Credential = {
  id: string;
  nickname: string | null;
  authenticator: string;
  backedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
};

type FlowResponse = { flowId: string; options: Record<string, unknown> };

class Session {
  user = $state<User | null>(null);
  credentials = $state<Credential[]>([]);
  loading = $state(true);
  offline = $state(false);

  async refresh(): Promise<void> {
    try {
      const me = await request<{ user: User | null; credentials?: Credential[] }>('/api/auth/me');
      this.user = me.user;
      this.credentials = me.credentials ?? [];
      this.offline = false;
    } catch (cause) {
      // Being offline is a state, never an error dialogue (brief §14a.1). Slice 10 replaces
      // this with the cached identity; the shape is already right.
      this.offline = cause instanceof OfflineError;
      if (!this.offline) this.user = null;
    } finally {
      this.loading = false;
    }
  }

  async register(username: string, invite: string | undefined): Promise<void> {
    const flow = await request<FlowResponse>('/api/auth/register/options', {
      method: 'POST',
      body: JSON.stringify({ username, ...(invite === undefined ? {} : { invite }) }),
    });

    // Anything the authenticator throws here — the user cancelled, no biometric, the name is
    // already registered on this device — is a message worth showing as it comes.
    const attestation = await startRegistration({ optionsJSON: flow.options as never });

    const result = await request<{ user: User }>('/api/auth/register/verify', {
      method: 'POST',
      body: JSON.stringify({ flowId: flow.flowId, response: attestation }),
    });
    this.user = result.user;
    await this.refresh();
  }

  async signIn(): Promise<void> {
    const flow = await request<FlowResponse>('/api/auth/login/options', { method: 'POST' });
    const assertion = await startAuthentication({ optionsJSON: flow.options as never });
    const result = await request<{ user: User }>('/api/auth/login/verify', {
      method: 'POST',
      body: JSON.stringify({ flowId: flow.flowId, response: assertion }),
    });
    this.user = result.user;
    await this.refresh();
  }

  async signOut(): Promise<void> {
    await request('/api/auth/logout', { method: 'POST', body: '{}' }).catch(() => undefined);
    this.user = null;
    this.credentials = [];
  }
}

export const session = new Session();

/** Turn whatever was thrown into one sentence a person can act on. */
export function describeError(cause: unknown): string {
  if (cause instanceof OfflineError) return 'Keine Verbindung. Versuch es gleich noch einmal.';
  if (cause instanceof ApiError) return cause.message;
  if (cause instanceof Error) {
    // WebAuthn's own errors are the useful ones here: a cancelled prompt, an authenticator
    // that already holds a key for this account, a browser without platform support.
    if (cause.name === 'NotAllowedError') return 'Abgebrochen — oder die Zeit war um.';
    if (cause.name === 'InvalidStateError') {
      return 'Für dieses Konto gibt es auf diesem Gerät schon einen Passkey.';
    }
    return cause.message;
  }
  return 'Etwas ist schiefgegangen.';
}
